import { makeAutoObservable, runInAction } from 'mobx';
import { ErrorStatusEnum, StatusMessageEnum, SubscriptionSourceEnum } from '@app/common';
import type { RootStore } from './root-store';
import { AuthDialogStore } from './auth-dialog-store';
import { ImpersonationDialogStore } from './impersonation-dialog-store';
import { UserProfileMobxDto } from '../mobx/dtos/user/user-profile-mobx-dto';
import { LoginUserMobxDto } from '../mobx/dtos/auth/login-user-mobx-dto';
import { MagicLinkIntentEnum, SubscriptionEnum } from '../../defs/api';
import { SignUpUserMobxDto } from '../mobx/dtos/auth/sign-up-user-mobx-dto';
import { ForgottenPasswordMobxDto } from '../mobx/dtos/auth/forgotten-password-mobx-dto';
import { routes } from '../../config/routes';

export type AuthStoreHydration = {
    userProfile: UserProfileMobxDto | null;
};

export class AuthStore {
    rootStore: RootStore;

    authDialogStore: AuthDialogStore;

    impersonationDialogStore: ImpersonationDialogStore;

    userProfile: UserProfileMobxDto | null = null;

    loading = false;

    registrationDone = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.authDialogStore = new AuthDialogStore(this);
        this.impersonationDialogStore = new ImpersonationDialogStore(this);
        makeAutoObservable(this, {
            rootStore: false,
            authDialogStore: false,
            impersonationDialogStore: false,
        });
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    hydrate(): AuthStoreHydration {
        return {
            userProfile: this.userProfile ? JSON.parse(JSON.stringify(this.userProfile)) : null,
        };
    }

    rehydrate(data: AuthStoreHydration): void {
        if (data.userProfile) {
            this.userProfile = UserProfileMobxDto.create(data.userProfile);
        }
    }

    async login(user: LoginUserMobxDto): Promise<boolean> {
        this.authDialogStore.setLoading(true);

        try {
            const res = await this.rootStore.apiClient.authController.authControllerLogin({ loginUserDto: user });
            if (res && res.accessToken) {
                this.rootStore.cookieService.setJwtToken(res.accessToken, user.rememberMe);
            }
            this.authDialogStore.setLoading(false);
            return true;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            if (e.message === ErrorStatusEnum.BadCredentials) {
                throw new Error(e.message);
            }
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            this.authDialogStore.setLoading(false);
            return false;
        }
    }

    get userHasFreeTariff(): boolean {
        return this.userProfile?.subscription === SubscriptionEnum.Free;
    }

    get userHasPaidTariff(): boolean {
        if (this.userProfile) {
            return [SubscriptionEnum.Premium, SubscriptionEnum.PremiumPlus].includes(this.userProfile?.subscription);
        }
        return false;
    }

    get userHasPremiumTariff(): boolean {
        return this.userProfile?.subscription === SubscriptionEnum.Premium;
    }

    get userHasPremiumPlusTariff(): boolean {
        return this.userProfile?.subscription === SubscriptionEnum.PremiumPlus;
    }

    get userHasPastDueTariff(): boolean {
        return this.userProfile?.subscription === SubscriptionEnum.PastDue;
    }

    get userHasAppleSubscription(): boolean {
        return this.userProfile?.subscriptionSource === SubscriptionSourceEnum.Apple;
    }

    logout(): void {
        this.rootStore.postCheckoutFlowStore.reset();
        this.rootStore.sourceSurveyStore.reset();
        if (this.rootStore.cookieService.hasJwtImpersonateToken()) {
            this.rootStore.cookieService.setImpersonateJwtToken(null);
        } else {
            this.rootStore.cookieService.setJwtToken(null);
            this.userProfile = null;
            this.rootStore.userStore.settings = null;
            this.rootStore.alertStore.clearAllMessages();
        }
    }

    async requestMagicLink(email: string, intent: MagicLinkIntentEnum): Promise<void> {
        this.authDialogStore.setLoading(true);
        this.authDialogStore.setMagicLinkRequested(false);
        this.rootStore.alertStore.clearAllMessages();
        const isSignUp = intent === MagicLinkIntentEnum.SignUp;
        const checkoutLookupKey = isSignUp ? this.rootStore.cookieService.getInterestedInSubscription() : null;

        try {
            const response = await this.rootStore.apiClient.authController.authControllerRequestMagicLink({
                magicLinkRequestDto: {
                    email,
                    intent,
                    ...(isSignUp
                        ? {
                              agreedTerms: this.authDialogStore.agreedTerms,
                              agreedMarketing: this.authDialogStore.agreedMarketing,
                              ...(checkoutLookupKey ? { checkoutLookupKey } : {}),
                          }
                        : {}),
                },
            });

            if (!response.accepted) {
                throw new Error(ErrorStatusEnum.Default);
            }

            if (checkoutLookupKey && this.rootStore.cookieService.getInterestedInSubscription() === checkoutLookupKey) {
                this.rootStore.cookieService.setInterestedInSubscription(null);
            }

            runInAction(() => {
                this.authDialogStore.setMagicLinkRequested(true);
            });
        } catch (e) {
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.authDialogStore.setLoading(false);
        }
    }

    async partnerSignUp(user: SignUpUserMobxDto, token: string): Promise<void> {
        this.authDialogStore.setLoading(true);

        try {
            await this.rootStore.apiClient.authExternalApi.authExternalControllerSignUp({
                externalSignUpUserDto: {
                    ...user,
                    token,
                },
            });
            runInAction(() => {
                this.registrationDone = true;
            });
        } catch (e) {
            this.authDialogStore.setLoading(false);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.authDialogStore.setLoading(false);
        }
    }

    async signUp(user: SignUpUserMobxDto): Promise<void> {
        this.authDialogStore.setLoading(true);

        try {
            await this.rootStore.apiClient.authController.authControllerSignUp({
                signUpUserDto: {
                    ...user,
                    interestedInSubscription:
                        this.rootStore.cookieService.getRedirectUrl() === routes.app.affiliate
                            ? null
                            : this.rootStore.cookieService.getSignupLookupKey(),
                },
            });
            runInAction(() => {
                this.registrationDone = true;
            });
        } catch (e) {
            this.authDialogStore.setLoading(false);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.authDialogStore.setLoading(false);
        }
    }

    async sendResetPasswordEmail(data: ForgottenPasswordMobxDto): Promise<void> {
        this.authDialogStore.setLoading(true);
        try {
            const res = await this.rootStore.apiClient.authController.authControllerSendResetPasswordEmail({
                resetPasswordTokenPayloadDto: data,
            });
            this.authDialogStore.setLoading(false);
            if (!res) {
                throw new Error(ErrorStatusEnum.EmailNotFound);
            }

            this.authDialogStore.setResetPasswordActive();
        } catch (e) {
            this.authDialogStore.setLoading(false);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            throw e;
        }
    }

    async resetPassword(token: string, password: string): Promise<void> {
        this.authDialogStore.setLoading(true);
        try {
            const res = await this.rootStore.apiClient.authController.authControllerResetPassword({
                resetPasswordDto: {
                    token,
                    password,
                },
            });
            if (!res) {
                throw new Error();
            }
        } finally {
            // errors handle in component
            this.authDialogStore.setLoading(false);
        }
    }

    async fetchUserProfile(): Promise<void> {
        this.startLoading();
        try {
            const userProfile = await this.rootStore.apiClient.authController.authControllerGetProfile();
            if (userProfile) {
                runInAction(() => {
                    this.userProfile = UserProfileMobxDto.create(userProfile);
                    this.rootStore.userStore.init();
                    this.rootStore.statisticStore.init();
                });

                await this.rootStore.criticalNotificationStore.fetchCriticalNotifications();
                await this.rootStore.userStore.fetchUserSettings();
            }
        } catch (e) {
            // no-op
        } finally {
            this.stopLoading();
        }
    }

    async sendVerificationEmail(email: string): Promise<void> {
        this.authDialogStore.setLoading(true);
        try {
            const res = await this.rootStore.apiClient.authController.authControllerSendVerificationLink({
                emailVerificationDto: { email },
            });
            if (!res) {
                throw new Error();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.authDialogStore.setLoading(false);
        }
    }

    async confirmEmail(accessToken: string): Promise<void> {
        this.authDialogStore.confirmLoading = true;
        this.authDialogStore.setLoginActive(true);
        try {
            const res = await this.rootStore.apiClient.authController.authControllerConfirmEmail({
                accessTokenDto: { accessToken },
            });

            if (res) {
                runInAction(() => {
                    this.authDialogStore.confirmDone = true;
                });
            }
        } catch (e) {
            if (e.message === StatusMessageEnum.EmailAlreadyConfirmed) {
                runInAction(() => {
                    this.authDialogStore.confirmDone = true;
                });
            } else {
                this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            }
        } finally {
            runInAction(() => {
                this.authDialogStore.confirmLoading = false;
            });
        }
    }

    async impersonation(email: string): Promise<void> {
        this.impersonationDialogStore.setLoading(true);
        try {
            const res = await this.rootStore.apiClient.authController.authControllerImpersonation({ email });
            if (res && res.accessToken) {
                this.rootStore.cookieService.setImpersonateJwtToken(res.accessToken);
                window.location.reload();
            }
        } finally {
            // errors handle in component
            this.impersonationDialogStore.setLoading(false);
        }
    }

    async deleteAccount(): Promise<void> {
        this.startLoading();
        try {
            const res = await this.rootStore.apiClient.userController.userControllerDeleteAccount();
            if (res) {
                window.location.href = routes.web.indexLogout;
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }
}
